import * as React from "react";
import { useRef, useEffect, useState } from "react";
import { RootStateOrAny, useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  DetailsListLayoutMode,
  Selection,
  IColumn,
  SelectionMode,
} from "office-ui-fabric-react/lib/DetailsList";
import { MarqueeSelection } from "office-ui-fabric-react/lib/MarqueeSelection";
import { ShimmeredDetailsList, Stack } from "office-ui-fabric-react";
import { IValidatorsListProps } from "../models/ValidatorsList.types";
import { footer, listS, listContainerS } from "../styles/ValidatorsList.styles";
import Pagination from "../providers/Pagination";
import { IRenderProps } from "../providers/Pagination.types";
import {
  setActiveValidator,
  setSelectedValidators,
} from "../../../config/redux/validators/validatorsActions";
import { toggleValidatorPanel } from "../../../config/redux/validators/validatorsPanel/validatorPanelActions";
import { generateColumns } from "../actions/generateColums";
import { NavigationPath } from "../../../config/routing/NavigationPath";
import { sort, toggleFiltration } from "../../../config/redux/filters/filterActions";

const ValidatorsList: React.FC<IValidatorsListProps> = ({ pathname, isPanelOpen }) => {
  const { t } = useTranslation("admin");
  const dispatch = useDispatch();
  const {
    validatorPanelOpen,
    selectedValidators,
    reloadValidators,
    remove,
    validatorAccepted,
    validatorCanceled,
  } = useSelector((state: RootStateOrAny) => state.validators);

  // Default sort (newest first) must be flagged on the rendered columns, otherwise the first header click does nothing
  const buildColumns = () => {
    const columns: IColumn[] = generateColumns(pathname, t) || [];
    const createdDateCol = columns.find(c => c.key === "createdDate");
    if (createdDateCol) {
      createdDateCol.isSortedDescending = true;
    }
    return columns;
  };

  const [cols, setCols] = useState<IColumn[]>(buildColumns);
  const footerRef = useRef<HTMLDivElement>(null);

  const selection = useRef(new Selection({
    onSelectionChanged: () => {
      if (!validatorPanelOpen) {
        const items = selection.current.getSelection();
        dispatch(setSelectedValidators({ selectedValidators: [...items] }));
      }
    },
  }));

  useEffect(() => {
    setCols(buildColumns());
  }, [t, pathname]);

  useEffect(() => {
    const createdDateCol = cols.find(c => c.key === "createdDate");
    if (createdDateCol) {
      dispatch(sort({ column: createdDateCol }));
    }
  }, []);

  useEffect(() => {
    if (reloadValidators || remove || validatorAccepted || validatorCanceled || !selectedValidators[0]) {
      selection.current.selectToKey("", true);
    }
  }, [reloadValidators, remove, validatorAccepted, validatorCanceled, selectedValidators]);

  useEffect(() => {
    return () => {
      dispatch(setSelectedValidators({ selectedValidators: [] }));
    };
  }, []);

  const onItemInvoked = (item: any) => {
    if (pathname !== NavigationPath.RECEPTION_PLACES_VALIDATORS) {
      dispatch(setActiveValidator(item));
      dispatch(toggleValidatorPanel(true));
    }
  };

  const onColumnHeaderClick = (_: any, column: any) => {
    const newColumns = cols.slice();
    const currColumn = newColumns.find(c => c.key === column.key)!;
    newColumns.forEach(col => {
      if (col === currColumn) {
        currColumn.isSortedDescending = !currColumn.isSortedDescending;
        currColumn.isSorted = true;
      } else {
        col.isSorted = false;
        col.isSortedDescending = true;
      }
    });
    dispatch(toggleFiltration({ filter: true }));
    dispatch(sort({ column: currColumn }));
    setCols(newColumns);
  };

  return (
    <>
      <Pagination
        footerRef={footerRef}
        render={({ data, loading }: IRenderProps) => (
          <>
            <Stack styles={listContainerS}>
              <MarqueeSelection isEnabled={!isPanelOpen} selection={selection.current}>
                <ShimmeredDetailsList
                  items={data}
                  columns={cols}
                  setKey="items"
                  layoutMode={DetailsListLayoutMode.justified}
                  selection={selection.current}
                  selectionPreservedOnEmptyClick={true}
                  onItemInvoked={onItemInvoked}
                  usePageCache={true}
                  styles={listS}
                  onColumnHeaderClick={onColumnHeaderClick}
                  enableShimmer={loading}
                  selectionMode={SelectionMode.single}
                />
              </MarqueeSelection>
            </Stack>
            <div className={footer} ref={footerRef} />
          </>
        )}
      />
    </>
  );
};

export default ValidatorsList;